import { assertUnreachable } from '@bedc/core';
import type { types } from '@bedc/types';

import { chatUtils } from './chatUtils';

type LinkOptions = {
	/**
	 * Maps a take-off id to its URL segment. The client passes its take-off path map; the
	 * server has none, so a canvas chat link degrades to the take-off overview.
	 */
	takeOffPath?: (takeOffId: types.wholeHouse.takeOff.Id) => string | undefined;
};

/**
 * The in-app path a notification opens, shared by the notification drawer and notification
 * emails so both always land on the same page. Undefined for announcements, which have no
 * destination.
 */
const getLink = (
	metadata: types.notification.metadata.Type,
	options: LinkOptions = {}
): string | undefined => {
	switch (metadata.type) {
		case 'announcement':
			return undefined;

		// Chat links to the transaction or job chat channel the @mention is in.
		case 'chat': {
			if (!metadata.chatChannelArn) {
				return '';
			}

			const channelId = chatUtils.getChannelIdFromArn(metadata.chatChannelArn);

			if (!metadata.transaction) {
				return `/job/${metadata.job.id}/chat/${channelId}`;
			}

			const transactionPath = `/transaction/${metadata.transaction.id}`;

			if (metadata.reviewItemId) {
				return `${transactionPath}/review/${metadata.reviewItemId}`;
			}

			if (metadata.canvasId) {
				const takeOffPath =
					metadata.imageId && metadata.takeOffId
						? options.takeOffPath?.(metadata.takeOffId)
						: undefined;

				return takeOffPath
					? `${transactionPath}/wh/${takeOffPath}/${metadata.imageId}`
					: `${transactionPath}/wh`;
			}

			return `${transactionPath}/chat/${channelId}`;
		}

		case 'job-assignment':
			return `/job/${metadata.job.id}`;

		// TODO(ROME-237): review, QA and rework assignments were meant to open
		// /transaction/:id/review, but the in-app code always overwrote that with the
		// transaction page. Kept as-is so in-app navigation doesn't change silently.
		case 'transaction-assignment':
		case 'transaction-unassigned':
		case 'transaction-completed':
		case 'transaction-reworked':
		case 'transaction-unallocated':
			return `/transaction/${metadata.transaction.id}`;

		case 'job-created':
		case 'job-completed':
			return `/job/${metadata.job.id}`;

		case 'fpc-job-completed':
			return metadata.job
				? `/job/${metadata.job.id}/fpc/${metadata.fpcJob.id}`
				: `/fpc/${metadata.fpcJob.id}`;

		// The copy's detail page: the completion summary, or the failure state with retry.
		case 'recipe-copy-completed':
		case 'recipe-copy-failed':
			return `/recipe-admin/copy/detail/${metadata.copyId}`;

		default:
			return assertUnreachable(metadata, 'unknown notification type');
	}
};

export const notificationUtils = { getLink };
